import { useEffect, useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";

export const TOGGLE_KEYS = {
  MULTI_SELECT_REASONS_FOR_JOINING: "multi_select_reasons_for_joining",
  COMMUNITY_INVITE_UI: "community_invite_ui",
} as const;

export const fetchToggles = async () => {
  const response = await fetch(
    `https://laborhack-feature-toggles.s3.amazonaws.com/toggles/${
      process.env.REACT_APP_APP_ENV
    }.json?v=${Date.now()}`
  );
  const toggles = await response.json();
  return toggles;
};

export function useToggles() {
  const [s3Toggles, setS3Toggles] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  const posthog = usePostHog();
  const [phFlagsLoaded, setPhFlagsLoaded] = useState(false);

  useEffect(() => {
    fetchToggles()
      .then((toggles) => {
        setS3Toggles(toggles);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!posthog) return;
    const unsubscribe = posthog.onFeatureFlags(() => {
      setPhFlagsLoaded(true);
    });
    return unsubscribe;
  }, [posthog]);

  // PostHog is authoritative once loaded; before that, fall through to S3.
  const toggles = useMemo(
    () =>
      new Proxy(s3Toggles, {
        get(target, prop: string) {
          if (typeof prop === "string" && posthog && phFlagsLoaded) {
            const posthogFlagValue = posthog.getFeatureFlag(prop);

            // Only let PostHog override when it has an explicit value for the flag.
            // If PostHog does not know this flag, fall back to the S3 toggle value.
            if (posthogFlagValue !== undefined) {
              return !!posthog.isFeatureEnabled(prop);
            }
          }
          return target[prop];
        },
      }),
    [s3Toggles, posthog, phFlagsLoaded]
  );

  return {
    toggles,
    isLoading,
  };
}
